"use client"

import { useEffect, useState, useRef, useMemo } from 'react'
import { useMap, Rectangle } from 'react-leaflet'
import L from 'leaflet'
import { useMapInteraction } from '@/contexts/MapInteractionContext'

// Draw Control Component - uses custom drag-based rectangle drawing
export function DrawControl() {
  const map = useMap()
  const { 
    mode, 
    setIsDrawing, 
    roiBounds, 
    setRoiBounds 
  } = useMapInteraction()
  
  // Track drawing state with refs to avoid rerenders
  const startPointRef = useRef<L.LatLng | null>(null)
  const [previewBounds, setPreviewBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null)
  const isDrawMode = mode === 'draw-roi'

  // Handle drawing with mousedown/mousemove/mouseup
  useEffect(() => {
    if (!map || !isDrawMode) return

    // Change cursor to crosshair when in draw mode
    map.getContainer().style.cursor = 'crosshair'
    
    // Disable map dragging while drawing
    const handleMouseDown = (e: L.LeafletMouseEvent) => {
      if (!isDrawMode) return
      
      // Disable map dragging
      map.dragging.disable()
      
      startPointRef.current = e.latlng
      setPreviewBounds({
        north: e.latlng.lat,
        south: e.latlng.lat,
        east: e.latlng.lng,
        west: e.latlng.lng
      })
      setIsDrawing(true)
    }

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      if (!startPointRef.current) return
      
      const start = startPointRef.current
      setPreviewBounds({
        north: Math.max(start.lat, e.latlng.lat),
        south: Math.min(start.lat, e.latlng.lat),
        east: Math.max(start.lng, e.latlng.lng),
        west: Math.min(start.lng, e.latlng.lng)
      })
    }

    const handleMouseUp = (e: L.LeafletMouseEvent) => {
      if (!startPointRef.current) return
      
      // Re-enable map dragging
      map.dragging.enable()
      
      const start = startPointRef.current
      
      // Calculate bounds from start and end points
      const north = Math.max(start.lat, e.latlng.lat)
      const south = Math.min(start.lat, e.latlng.lat)
      const east = Math.max(start.lng, e.latlng.lng)
      const west = Math.min(start.lng, e.latlng.lng)
      
      // Only set bounds if the rectangle has some size
      if (north !== south && east !== west) {
        setRoiBounds({ north, south, east, west })
      }
      
      startPointRef.current = null
      setPreviewBounds(null)
      setIsDrawing(false)
    }

    map.on('mousedown', handleMouseDown)
    map.on('mousemove', handleMouseMove)
    map.on('mouseup', handleMouseUp)

    return () => {
      map.off('mousedown', handleMouseDown)
      map.off('mousemove', handleMouseMove)
      map.off('mouseup', handleMouseUp)
      map.getContainer().style.cursor = ''
      map.dragging.enable()
    }
  }, [map, isDrawMode, setIsDrawing, setRoiBounds])

  // Reset cursor when mode changes
  useEffect(() => {
    if (!map) return
    if (!isDrawMode) {
      map.getContainer().style.cursor = ''
      startPointRef.current = null
      setPreviewBounds(null)
    }
  }, [map, isDrawMode])

  // Memoize rectangle bounds to prevent unnecessary rerenders
  const previewRectBounds = useMemo(() => {
    if (!previewBounds || previewBounds.north === previewBounds.south || previewBounds.east === previewBounds.west) {
      return null
    }
    return [
      [previewBounds.south, previewBounds.west],
      [previewBounds.north, previewBounds.east]
    ] as [[number, number], [number, number]]
  }, [previewBounds])

  const finalRectBounds = useMemo(() => {
    if (!roiBounds) return null
    return [
      [roiBounds.south, roiBounds.west],
      [roiBounds.north, roiBounds.east]
    ] as [[number, number], [number, number]]
  }, [roiBounds])

  return (
    <>
      {/* Preview rectangle while drawing */}
      {previewRectBounds && (
        <Rectangle
          bounds={previewRectBounds}
          pathOptions={{
            color: '#3b82f6',
            weight: 2,
            fillOpacity: 0.1,
            fillColor: '#3b82f6',
            dashArray: '5, 5'
          }}
        />
      )}
      
      {/* Final ROI rectangle */}
      {finalRectBounds && (
        <Rectangle
          bounds={finalRectBounds}
          pathOptions={{
            color: '#3b82f6',
            weight: 3,
            fillOpacity: 0.15,
            fillColor: '#3b82f6',
            dashArray: '5, 5'
          }}
        />
      )}
    </>
  )
}

